import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { Badge, IconButton, Menu, Tooltip } from '@mui/material'
import clsx from 'clsx'
import headerIconStyle from 'components/Header/HeaderIconButton.module.css'
import { useFmsNotificationsContext } from 'context/FmsNotificationsContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'

import { NotificationsInboxPanel, useFeaturedNotificationId } from './NotificationsInboxPanel'

import style from './FmsNotifications.module.css'

/** Auto-close dropdown after this duration (matches per-item toast highlight window). */
const PANEL_AUTO_CLOSE_MS = 10_000

function formatSnoozeUntil(until: Date): string {
  try {
    return until.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return until.toISOString()
  }
}

/** Header bell + unread inbox popover (separate from the account avatar menu). */
export function FmsNotifications() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    unreadCount,
    hideAllToastHighlights,
    items,
    isSnoozed,
    mutedUntil,
    suppressSandwichAutoOpen,
    quietArrivalNonce,
  } = useFmsNotificationsContext()
  const featuredId = useFeaturedNotificationId()

  const bellRef = useRef<HTMLButtonElement>(null)
  const panelCloseTimer = useRef<number | null>(null)
  const panelHoveredRef = useRef(false)
  const openedByToastRef = useRef(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  /** Toast-opened panel should not steal focus (avoids aria-hidden warning on auto-close). */
  const [openedByToast, setOpenedByToast] = useState(false)
  const [bellPulse, setBellPulse] = useState(false)
  const panelOpen = Boolean(anchorEl)

  const isCompact = useMemo(() => {
    const hasUnread = items.some(i => !i.read)
    return items.length === 0 || !hasUnread
  }, [items])

  const clearPanelCloseTimer = useCallback(() => {
    if (panelCloseTimer.current != null) {
      window.clearTimeout(panelCloseTimer.current)
      panelCloseTimer.current = null
    }
  }, [])

  const closePanel = useCallback(() => {
    clearPanelCloseTimer()
    openedByToastRef.current = false
    setOpenedByToast(false)
    // Blur focused descendant before MUI sets aria-hidden on the Modal root.
    const active = document.activeElement
    if (active instanceof HTMLElement) {
      const inMenu = active.closest('.MuiModal-root, .MuiMenu-root, .MuiPopover-root')
      if (inMenu) {
        active.blur()
      }
    }
    setAnchorEl(null)
  }, [clearPanelCloseTimer])

  const schedulePanelAutoClose = useCallback(() => {
    clearPanelCloseTimer()
    if (!panelOpen || !openedByToastRef.current || panelHoveredRef.current) {
      return
    }
    panelCloseTimer.current = window.setTimeout(() => {
      panelCloseTimer.current = null
      closePanel()
      hideAllToastHighlights()
    }, PANEL_AUTO_CLOSE_MS)
  }, [panelOpen, closePanel, hideAllToastHighlights, clearPanelCloseTimer])

  useEffect(() => {
    if (suppressSandwichAutoOpen) {
      // Clear leftover toastVisible so featuredId cannot reopen after remount/flicker.
      hideAllToastHighlights()
      if (openedByToastRef.current) {
        closePanel()
      }
      return
    }
    if (featuredId != null && bellRef.current) {
      openedByToastRef.current = true
      setOpenedByToast(true)
      setAnchorEl(bellRef.current)
    }
  }, [featuredId, suppressSandwichAutoOpen, closePanel, hideAllToastHighlights])

  useEffect(() => {
    if (quietArrivalNonce <= 0) {
      return
    }
    setBellPulse(false)
    const id = window.requestAnimationFrame(() => setBellPulse(true))
    return () => window.cancelAnimationFrame(id)
  }, [quietArrivalNonce])

  useEffect(() => {
    if (!panelOpen) {
      panelHoveredRef.current = false
      return
    }
    schedulePanelAutoClose()
    return clearPanelCloseTimer
  }, [panelOpen, schedulePanelAutoClose, clearPanelCloseTimer])

  const handlePanelMouseEnter = useCallback(() => {
    panelHoveredRef.current = true
    clearPanelCloseTimer()
  }, [clearPanelCloseTimer])

  const handlePanelMouseLeave = useCallback(() => {
    panelHoveredRef.current = false
    schedulePanelAutoClose()
  }, [schedulePanelAutoClose])

  const handlePanelClose = () => {
    closePanel()
    hideAllToastHighlights()
  }

  const bellTooltip = isSnoozed && mutedUntil
    ? t('header.notifications_snoozed', { until: formatSnoozeUntil(mutedUntil) })
    : t('header.notifications')

  return (
    <>
      <Tooltip title={bellTooltip}>
        <IconButton
          ref={bellRef}
          className={headerIconStyle.button}
          aria-label={bellTooltip}
          size="medium"
          onClick={e => {
            if (panelOpen) {
              handlePanelClose()
              return
            }
            openedByToastRef.current = false
            setOpenedByToast(false)
            setAnchorEl(e.currentTarget)
          }}
          data-test-id={getTestId('notifications-bell')}
          data-snoozed={isSnoozed ? 'true' : 'false'}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <span
              className={clsx(style.bellIconWrap, bellPulse && style.bellPulse)}
              onAnimationEnd={() => setBellPulse(false)}
            >
              {isSnoozed ? (
                <NotificationsOffIcon className={headerIconStyle.icon} />
              ) : (
                <NotificationsIcon className={headerIconStyle.icon} />
              )}
            </span>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={panelOpen}
        onClose={handlePanelClose}
        // Toast auto-open: keep focus on the page; click-open still focuses the menu.
        disableAutoFocus={openedByToast}
        disableEnforceFocus={openedByToast}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ disablePadding: true, autoFocusItem: false }}
        PaperProps={{
          className: clsx(style.menuPaper, isCompact && style.menuPaperCompact),
          onMouseEnter: handlePanelMouseEnter,
          onMouseLeave: handlePanelMouseLeave,
          'data-test-id': getTestId('notifications-inbox-popover'),
        }}
      >
        <NotificationsInboxPanel
          compact={isCompact}
          featuredId={featuredId}
          onRequestClose={closePanel}
          onOpenHistory={() => {
            closePanel()
            hideAllToastHighlights()
            navigate(PATH.ACCOUNT_NOTIFICATIONS)
          }}
        />
      </Menu>
    </>
  )
}
